import React from 'react';
import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import MultiOrgDropDown from './MultiOrg';
import { LoadingSkeletonGroupWise, LoadingSkeletonTableWise } from '../components/LoadingSkeleton';
import { useSelector } from 'react-redux';
import { selectEmail } from '../reducer/authSlice';

const useStyles = makeStyles((theme) => ({
  root: { flexGrow: 1 },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    '@media (max-width: 600px)': { minHeight: '120px' },
  },
  content: {
    flexGrow: 80,
    padding: theme.spacing(3),
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    '@media (max-width: 600px)': {
      padding: theme.spacing(2),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  buttonContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: '0',
    marginBottom: '16px',
    '@media (max-width: 600px)': { gap: '0', marginBottom: '12px' },
  },
  btnPadding: {
    marginRight: '8px',
    marginBottom: '8px',
    fontSize: '0.8rem',
    fontWeight: 500,
    minHeight: '36px',
    borderRadius: '8px',
    textTransform: 'none',
    padding: '6px 16px',
    transition: 'all 0.2s ease',
  },
  notice: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#fff3e0',
    border: '1px solid #ffb74d',
    borderLeft: '4px solid #f57c00',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    fontSize: '14px',
    color: '#e65100',
    fontWeight: 500,
  },
  tableRoot: {
    background: 'transparent',
    boxShadow: 'none',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    border: '1px solid #e8e8e8',
  },
  listHeader: {
    display: 'grid',
    gridTemplateColumns: '2.5fr 1.5fr',
    gap: '24px',
    padding: '14px 24px',
    background: '#fafafa',
    borderBottom: '1px solid #e8e8e8',
    fontSize: '12px',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    '@media (max-width: 768px)': { display: 'none' },
  },
  listItem: {
    display: 'grid',
    gridTemplateColumns: '2.5fr 1.5fr',
    gap: '24px',
    padding: '16px 24px',
    alignItems: 'center',
    borderBottom: '1px solid #f0f0f0',
    borderLeft: '3px solid #bdbdbd',
    '&:last-child': { borderBottom: 'none' },
    '@media (max-width: 768px)': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '8px',
      padding: '16px 20px',
    },
  },
  testName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#555',
    margin: 0,
    lineHeight: 1.4,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    background: 'rgba(97,97,97,0.1)',
    color: '#616161',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 24px',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '12px',
    border: '1px solid #e8e8e8',
  },
  emptyTitle: {
    margin: '0 0 8px 0',
    fontSize: '17px',
    fontWeight: 600,
    color: '#333',
  },
  emptyText: {
    margin: 0,
    fontSize: '14px',
    color: '#666',
    maxWidth: '300px',
    lineHeight: 1.5,
  },
}));

export default function DisabledDashboardGrid(props) {
  const classes = useStyles();
  const email = useSelector(selectEmail);
  const {
    handleIconCLick, loading, buttonArray, buttonName, setButtonClicked,
    typeButton, setSelectedType, selectTypeButton, handleHome,
    menuTitle, setMenuTitle, selectedTab, setSelectedTab,
  } = props;

  const [tableData, setTableData] = React.useState({});

  const handleGroupClick = (item) => {
    setButtonClicked(item);
    if (buttonArray['DISABLED'] !== undefined) {
      setTableData(buttonArray['DISABLED'][item]);
    }
  };

  React.useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      setMenuTitle(['Disabled Groups', 'Disabled Feedback', 'Disabled Survey']);
      setSelectedType('DISABLED');
    }
    const disabledData = buttonArray['DISABLED'];
    if (disabledData && Object.keys(disabledData).length > 0) {
      const firstGroup = Object.keys(disabledData)[0];
      if (isMounted) {
        setButtonClicked(firstGroup);
        setTableData(disabledData[firstGroup]);
      }
    } else {
      if (isMounted) setTableData({});
    }
    return () => { isMounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonArray, typeButton]);

  const rows = tableData?.tableData || [];

  return (
    <main className={classes.content}>
      <div className={classes.toolbar} />
      <b>Login ID:</b> {email}
      <br /><br />
      <div className="row">
        <MultiOrgDropDown
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          handleIconCLick={handleIconCLick}
          handleHome={handleHome}
          typeButton={typeButton}
          menuTitle={menuTitle}
        />
      </div>
      <br />

      <div className={classes.notice}>
        &#9888;&nbsp; The test window for these assessments has been closed. Please contact your administrator.
      </div>

      {loading ? <LoadingSkeletonGroupWise /> : (
        <Grid style={{ paddingBottom: '0px' }} item xs={12}>
          <div className={classes.buttonContainer}>
            {buttonArray['DISABLED'] !== undefined
              ? Object.keys(buttonArray['DISABLED']).map((item, index) => (
                <Button
                  key={`dButton${index}`}
                  className={classes.btnPadding}
                  variant="outlined"
                  color={buttonName === item ? 'primary' : 'default'}
                  style={{ background: buttonName === item ? 'rgba(111,41,129,0.13)' : '' }}
                  onClick={() => handleGroupClick(item)}
                >
                  {item}
                </Button>
              ))
              : null}
          </div>
        </Grid>
      )}

      <Grid item xs={12}>
        {loading ? <LoadingSkeletonTableWise /> : (
          <Paper className={classes.tableRoot}>
            {rows.length > 0 ? (
              <div className={classes.listContainer}>
                <div className={classes.listHeader}>
                  <span>Test Name</span>
                  <span>Scheduled Date</span>
                </div>
                {rows.map((row, index) => (
                  <div key={`disabled-${index}`} className={classes.listItem}>
                    <p className={classes.testName}>{row.name}</p>
                    <span className={classes.statusBadge}>
                      {row.date || 'N/A'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className={classes.emptyState}>
                <p className={classes.emptyTitle}>No Disabled Groups</p>
                <p className={classes.emptyText}>
                  No pending tests from disabled groups found.
                </p>
              </div>
            )}
          </Paper>
        )}
      </Grid>
    </main>
  );
}
